import { useState } from 'react';
import { GOOGLE_API_KEY } from 'src/common/constants';
import { getLocations } from 'src/services/client';

export interface ResolvedAddress {
  addressLine1?: string;
  city?: string;
  province?: string;
  postalCode?: string;
}

interface UseGeocoderArgs {
  onAddressResolved: (address: ResolvedAddress) => void;
}

export interface UseGeocoderReturn {
  location: string;
  predictions: any[];
  getLocation: (value: string) => Promise<void>;
  onClickLocation: (address: string) => Promise<void>;
  clearPredictions: () => void;
}

/**
 * Reusable address autocomplete + geocoding hook.
 *
 * Owns only the autocomplete query state and geocode round-trip. Does not
 * know about any form's data shape — callers pass an `onAddressResolved`
 * callback that receives the resolved fields to merge into their own state.
 */
export const useGeocoder = ({
  onAddressResolved,
}: UseGeocoderArgs): UseGeocoderReturn => {
  const [predictions, setPredictions] = useState<any[]>([]);
  const [location, setLocation] = useState<string>('');

  const clearPredictions = (): void => {
    setPredictions([]);
  };

  const getLocation = async (value: string): Promise<void> => {
    setLocation(value);

    getLocations(value).then((response: any) => {
      if (response.status === 200) {
        response.json().then((responseJson: any) => {
          let data = JSON.parse(responseJson);
          setPredictions(data.predictions);
        });
      } else {
        setPredictions([]);
      }
    });
  };

  const onClickLocation = async (address: string): Promise<void> => {
      const apiurl = `https://maps.google.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&address=${address}`;

      try {
        const result = await fetch(apiurl);
        const json = await result.json();
        let latitude = json.results[0].geometry.location.lat;
        let longitude = json.results[0].geometry.location.lng;
        const apiurl1 = `https://maps.google.com/maps/api/geocode/json?key=${GOOGLE_API_KEY}&latlng=${latitude},${longitude}`;

        const getResult = await fetch(apiurl1);
        const addressObj = await getResult.json();
        let addressLine1;
        let city;
        let state;
        let pincode;
        let addressData = addressObj.results[0].address_components;

        if (addressData && addressData.length > 0) {
          addressData.forEach((data: any) => {
            if (data.types.indexOf('street_number') != -1) {
              addressLine1 = data.long_name;
            }
            if (data.types.indexOf('route') != -1) {
              addressLine1 = addressLine1
                ? addressLine1 + ', ' + data.long_name
                : data.long_name;
            }
            if (data.types.indexOf('neighborhood') != -1) {
              addressLine1 = addressLine1
                ? addressLine1 + ', ' + data.long_name
                : data.long_name;
            }
            if (data.types.indexOf('sublocality_level_2') != -1) {
              addressLine1 = addressLine1
                ? addressLine1 + ', ' + data.long_name
                : data.long_name;
            }
            if (data.types.indexOf('sublocality_level_1') != -1) {
              addressLine1 = addressLine1
                ? addressLine1 + ', ' + data.long_name
                : data.long_name;
            }
            if (data.types.indexOf('locality') != -1) {
              city = data.long_name;
            }
            if (data.types.indexOf('administrative_area_level_1') != -1) {
              state = data.long_name;
            }
            if (data.types.indexOf('postal_code') != -1) {
              pincode = data.long_name;
            }
          });

          onAddressResolved({
            addressLine1: addressLine1,
            city: city,
            province: state,
            postalCode: pincode,
          });
          setPredictions([]);
          setLocation('');
        }
      } catch (err) {
        console.error(err);
      }
  };

  return {
    location,
    predictions,
    getLocation,
    onClickLocation,
    clearPredictions,
  };
};
